import { Component, inject, Inject } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, FormArray, Validators } from '@angular/forms';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { map, catchError, of, forkJoin } from 'rxjs';
import { FileDisplayComponent } from 'src/app/shared/components/file-display/file-display.component';
import { FileUploadData } from 'src/app/shared/components/floating-file-viewer/floating-file-viewer.component';
import { FileUploadService } from 'src/app/shared/service/disbursment/file-upload.service';
import { ConfirmationDialogComponent } from '../../dis-repository/pages/create-port-tariff/confirmation-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { DisbursmentService } from 'src/app/shared/service/disbursment/disbursment.service';
import { fileUploadEnvironment } from 'src/app/shared/constants/file-upload.constants';
import { md5 } from 'js-md5';
// @ts-ignore
import { saveAs } from 'file-saver';
@Component({
  selector: 'app-file-upload-dis',
  templateUrl: './file-upload-dis.component.html',
  styleUrls: ['./file-upload-dis.component.scss']
})
export class FileUploadDisComponent {
selectedFiles: File[] = [];
  maxFileSizeMB: number = 5;
  allowedTypes: string = '.pdf,.jpg,.jpeg,.png';
  allowedMimeTypes: string[] = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];
  errorMessage: string = '';
  successMessage: string = '';
  isLoading: boolean = false;
  isDragOver: boolean = false;
  previewUrl: SafeResourceUrl | null = null;
  isUploadMode: boolean = true;
  previewFiles: FileUploadData[] = [];
  fileHash: string = '';
  private loader = inject(DisbursmentService);
  fileUploadForm: FormGroup;
  fileUploadFor = new FormControl('');
  isDisabled = true;
  pdaPreviewFiles: FileUploadData[] = [];
  fdaPreviewFiles: FileUploadData[] = [];

  constructor(
    private dialogRef: MatDialogRef<any>,
    private snackBar: ToastrService,
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public existingdata: any,
    private fileUploadService: FileUploadService
  ) {
    this.isUploadMode = existingdata?.isUploadMode ?? true;
    this.selectedFiles = existingdata?.files || [];

    this.fileUploadForm = this.fb.group({
      files: this.fb.array([])
    });

    this.existingdata.isDisbursment ? this.isDisabled = true : this.isDisabled = false;

    this.fileUploadFor.valueChanges.subscribe(value => {

      if (value === 'FDA' && this.existingdata.fdaId === null) {
        this.snackBar.warning('Please create FDA first before uploading files.');
        this.isDisabled = true;

      }
      else if (value === 'PDA' && this.existingdata.pdaId === null) {
        this.snackBar.warning('Pda is not Found for this disbursement.');
        this.isDisabled = true;

      }
      else {
        this.isDisabled = false;
      }

    });

  }
  get filesArray(): FormArray {
    return this.fileUploadForm.get('files') as FormArray;
  }


  ngOnInit(): void {
    this.getUploadedFiles();
  }

  // close Dialog for the component when used for upload by Rahul
  closeDialog(): void {
    this.dialogRef.close();
  }

  // Drag and Drop Handlers by Rahul
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) this.processFiles(Array.from(files));
  }
  // End of Drag and Drop Handlers by Rahul


  //After Selecting files using file input by Rahul
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files) {
      const selectingFiles: File[] = Array.from(files);
      this.clearMessages();
      this.processFiles(selectingFiles);
    }
  }

  // method to process the selected files
  processFiles(files: File[]): void {
    this.clearMessages();
    const validFiles = files.filter(file => this.validateFile(file)).map(file => {
      const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
      const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.'));
      const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
      const newFileName = `${fileNameWithoutExtension}_${timestamp}${fileExtension}`;
      const renamedFile = new File([file], newFileName, { type: file.type });
      return renamedFile;
    });
    validFiles.forEach(() => {
      this.filesArray.push(this.fb.group({
        description: ['', Validators.required]
      }));
    });

    this.selectedFiles = [...this.selectedFiles, ...validFiles];
    validFiles.length > 0 ? this.successMessage = `${this.selectedFiles.length} valid file(s) processed` : '';
  }


  // method to validate the selected file by Rahul
  validateFile(file: File): boolean {
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');

    if (nameWithoutExt.length > 90) {
      this.snackBar.error("File name length exceeded files are not allowed.");
      return false;
    }


    if (file.size > this.maxFileSizeMB * 1024 * 1024) {
      this.snackBar.error(" File size exceeded files are not allowed.")
      return false;
    }
    if (!this.allowedMimeTypes.includes(file.type)) {
      this.errorMessage = `${file.name} is invalid file type`;
      this.snackBar.error("Invalid file types are not allowed")
      return false;
    }
    return true;
  }

  //After Validating File submit for upload by Rahul
  async upload(): Promise<void> {
    if (!this.selectedFiles.length) return;
    this.isLoading = true;
    this.clearMessages();

    // Generate file paths for all selected files
    const filePaths = this.generateFilePath(
      fileUploadEnvironment,
      this.existingdata.disbursementId,
      this.existingdata.isDisbursment
        ? (this.fileUploadFor.value === "PDA"
          ? `PDA-${this.existingdata.pdaId}`
          : this.fileUploadFor.value === "FDA"
            ? `FDA-${this.existingdata.fdaId}`
            : '')
        : (this.existingdata.pdaOrFda === 'PDA'
          ? `PDA-${this.existingdata.pdaId}`
          : `FDA-${this.existingdata.fdaId}`),
      this.selectedFiles
    );

    const data = { file_path: filePaths };

    // Get presigned URLs for the files
    this.fileUploadService.getPresignedUrl(data).subscribe({
      next: (res) => {
        const presignedUrls = res.presigned_url;
        const uploadObservables = this.selectedFiles.map((file, index) => {
          return this.fileUploadService.uploadImageToS3(presignedUrls[index], file).pipe(
            map(() => ({ success: true, file, index })),
            catchError((err) => {
              return of({ success: false, file, index, error: err });
            })
          );
        });

        // Wait for all file uploads to complete
        forkJoin(uploadObservables).subscribe({
          next: async (results) => {
            const failedUploads = results.filter(result => !result.success);
            const successfulUploads = results.filter(result => result.success);
            
            if (failedUploads.length > 0) {
              this.isLoading = false;
              const failedFileNames = failedUploads.map(result => result.file.name).join(', ');
              this.snackBar.error(`Failed to upload: ${failedFileNames}`);
              return;
            }

            // Only save metadata for successfully uploaded files
            const metadata = {
              pda_fda_flag: this.existingdata.isDisbursment ? this.fileUploadFor.value : this.existingdata?.pdaOrFda,
              disbursement_seq: this.existingdata?.disbursement_seq,
              source_type: this.existingdata?.source_type || '',
              files: await Promise.all(successfulUploads.map(async (result) => ({
                file_name: result.file.name,
                md5_file: await this.generateMD5Hash(result.file),
                file_path: filePaths[result.index],
                file_description: this.fileUploadForm.value.files[result.index].description,
                sync: 'N'
              }))),
            };
            await this.saveFileMetadataToDB(metadata);
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.snackBar.error('Upload failed. Please try again.');
          }
        });
      },
      error: (err) => {
        this.isLoading = false;
        const errMsg = err?.error?.error || "Something went wrong. Please try again later.";
        this.snackBar.error(errMsg);
      }
    });
  }

  // Function to generate MD5 hash for the file (you need to implement or import an MD5 hashing library)
  generateMD5Hash(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const md5Hash = md5(arrayBuffer);
        resolve(md5Hash);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  // method to save the filedata after uloading the file to S3 bucket
  saveFileMetadataToDB(metadata: any): void {
    this.loader.showDialogLoader();
    this.fileUploadService.fileUploadToDb(metadata).subscribe({
      next: (res) => {
        this.snackBar.success(res.message || 'File metadata saved to DB successfully');
        this.loader.hideDialogLoader();
        this.isLoading = false;
        this.selectedFiles = [];
        this.filesArray.clear();
        this.getUploadedFiles();
      },
      error: (err) => {
        this.isLoading = false;
        const errMsg = err?.error?.error || "Error saving file metadata to DB";
        this.snackBar.error(errMsg);
        this.loader.hideDialogLoader();
        this.isLoading = false;

      }
    });
  }

  // method to generate the file path for the files to be uploaded
  generateFilePath(env: string, dId: number, pdaId: string, files: File[]): string[] {
    return files.map((file: File) => `${env}/${dId}/${pdaId}/${file.name}`);
  }

  // File Preview Functionality 
  preview(file: File | FileUploadData, uploadOrPreview: string): void {
    if (this.canPreview(file, uploadOrPreview)) {
      let url: string | undefined = undefined;

      if (file instanceof File) {
        url = URL.createObjectURL(file);
        this.openPreviewDialog(url, file.name);
      } else if ('presigned_url' in file && typeof (file as any).presigned_url === 'string') {
        this.previewWithRetry(file as FileUploadData, (file as any).presigned_url);
        return;
      }
      
      if (!url) {
        this.snackBar.error('Cannot preview this file.');
        return;
      }
    }
  }

  private previewWithRetry(file: FileUploadData, presignedUrl: string): void {
    fetch(presignedUrl, { method: 'GET' })
      .then(response => {
        if (response.status === 403) {
          // URL expired, get new presigned URL
          this.refreshPresignedUrlAndPreview(file);
          return;
        }
        if (response.status === 404) {
          this.snackBar.error('File not found.');
          return;
        }
        if (response.ok) {
          this.openPreviewDialog(presignedUrl, file.name);
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      })
      .catch(() => {
        this.snackBar.error('Cannot preview this file.');
      });
  }

  private refreshPresignedUrlAndPreview(file: FileUploadData): void {
    const data = {
      disbursement_seq: this.existingdata?.disbursement_seq,
      pda_fda_flag: this.existingdata.isDisbursment ? "DIS" : this.existingdata?.pdaId ? 'PDA' : 'FDA',
    };

    this.fileUploadService.fileDataFromDb(data).subscribe({
      next: (res) => {
        const updatedFile = res.find((f: any) => f.file_id === file.file_id);
        if (updatedFile?.presigned_url) {
          this.openPreviewDialog(updatedFile.presigned_url, file.name);
        } else {
          this.snackBar.error('Unable to refresh preview link.');
        }
      },
      error: () => {
        this.snackBar.error('Error refreshing preview link.');
      }
    });
  }

  private openPreviewDialog(url: string, fileName: string): void {
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    const data = {
      url: this.previewUrl,
      name: fileName,
    };
    this.dialog.open(FileDisplayComponent, {
      data: data,
      width: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      maxWidth: '100vw',
    });
  }

  // Remove file from the selected list by Rahul
  removeFile(index: number): void {
    if (!this.isUploadMode && this.selectedFiles.length === 1) {
      this.snackBar.success('File deleted successfully');
    } else {
      this.selectedFiles.splice(index, 1);
      this.clearMessages();
    }
  }

  // Cancel the upload and clear selected files by Rahul
  cancel(): void {
    this.selectedFiles = [];
    this.clearMessages();
  }

  // Clear messages by Rahul
  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }


  //Allow preview for images and PDFs only in upload mode and Allow preview for images in preview mode
  canPreview(file: File | FileUploadData, uploadOrPreview: string): boolean {
    if (uploadOrPreview === 'upload' && file instanceof File) {
      return file.type.startsWith('image/') || file.type === 'application/pdf';
    }
    if (uploadOrPreview === 'preview' && 'presigned_url' in file && typeof (file as FileUploadData).presigned_url === 'string') {
      const previewFile = file as FileUploadData;
      const presignedUrl = previewFile.presigned_url;
      const containsValidExtension = presignedUrl.includes('.pdf') ||
        presignedUrl.includes('.png') ||
        presignedUrl.includes('.jpg') ||
        presignedUrl.includes('.jpeg');

      return containsValidExtension;
    }
    return false;
  }

  // method to get the uploaded files from DB
  getUploadedFiles(): void {
    const data = {
      disbursement_seq: this.existingdata?.disbursement_seq,
      pda_fda_flag: this.existingdata.isDisbursment ? "DIS" : this.existingdata?.pdaId ? 'PDA' : 'FDA',
    }
    this.loader.showDialogLoader();
    this.fileUploadService.fileDataFromDb(data).subscribe({
      next: (res) => {
        this.existingdata.isDisbursment ? this.assignPreviewFiles(res) : this.previewFiles = res || [];
        this.loader.hideDialogLoader();
        // this.selectedFiles = [];
      },
      error: (err) => {
        this.loader.hideDialogLoader();

        const errMsg = err?.error?.error || "Error fetching files from DB";
        this.snackBar.error(errMsg);
      }
    });

  }


  // method to assign preview files to PDA and FDA arrays
  assignPreviewFiles(res: FileUploadData[]): void {
    this.pdaPreviewFiles = res.filter(file => {
      return file.complete_file_path?.includes('/PDA-');
    });
    this.fdaPreviewFiles = res.filter(file => {
      return file.complete_file_path?.includes('/FDA-');
    });

    this.previewFiles = [...this.pdaPreviewFiles, ...this.fdaPreviewFiles];
  }


  // method to delete the  selected file
  deleteSelectedFile(file: any): void {
    const dialogRefChild = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Confirm Delete',
        text: 'Do you want to delete the file?'
      }
    });
    dialogRefChild.afterClosed().subscribe(result => {
      if (result) {
        this.loader.showDialogLoader();
        this.fileUploadService.deleteFileFromDb(file.file_id).subscribe({
          next: (res) => {
            this.loader.showDialogLoader();
            this.snackBar.success(res.message || 'File deleted successfully');
            this.getUploadedFiles();
          },
          error: (err) => {
            this.loader.hideDialogLoader();
            const errMsg = err?.error?.error || "Error deleting file from DB";
            this.snackBar.error(errMsg);
          }
        });
      }
    });
  }

  // method to download the file from S3 using presigned URL
  downloadFile(file: any): void {
    this.downloadWithRetry(file, file.presigned_url);
  }

  private downloadWithRetry(file: any, presignedUrl: string): void {
    if (!presignedUrl) {
      this.snackBar.error('Error while downloading the file.');
      return;
    }

    fetch(presignedUrl)
      .then(response => {
        if (response.status === 403) {
          // URL expired, get new presigned URL
          this.refreshPresignedUrlAndDownload(file);
          return;
        }
        if (response.status === 404) {
          this.snackBar.error('File not found.');
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        if (blob) {
          saveAs(blob, file.name);
        }
      })
      .catch(err => {
        this.snackBar.error('Error while downloading the file.');
      });
  }

  private refreshPresignedUrlAndDownload(file: any): void {
    const data = {
      disbursement_seq: this.existingdata?.disbursement_seq,
      pda_fda_flag: this.existingdata.isDisbursment ? "DIS" : this.existingdata?.pdaId ? 'PDA' : 'FDA',
    };

    this.fileUploadService.fileDataFromDb(data).subscribe({
      next: (res) => {
        const updatedFile = res.find((f: any) => f.file_id === file.file_id);
        if (updatedFile?.presigned_url) {
          this.downloadWithRetry(file, updatedFile.presigned_url);
        } else {
          this.snackBar.error('Unable to refresh download link.');
        }
      },
      error: () => {
        this.snackBar.error('Error refreshing download link.');
      }
    });
  }

  // viewFile(file: any): void {
  //   const presignedUrl = file.presigned_url;
  //   if (presignedUrl) {
  //     window.open(presignedUrl, '_blank');
  //   } else {
  //   }
  // }

}
